import { axiosClient } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { needOwnerToPurchasePortalPopup } from '@nx-frontend/shared/popups/need-owner-to-purchase-portal-popup';
import {
  DialogPopupPayload,
  OrgMemberRoleAndPrivileges,
  OrgServiceOrgUsageDto,
  PaywallPopupPayload,
  PopupType,
  PortalCreateWorkspaceParams,
  Workspace,
} from '@nx-frontend/shared/types';
import { Org, PortalServicePortalDto, PortalServicePortalsUsageDto } from '@nx-frontend/shared/types';
import { ModalState, OpenModalFn } from '@nx-frontend/shared/types';
import { getPortalWizardUrl } from '@nx-frontend/shared/utils';
import { NextRouterOptions, NextRouterPushFunction, NextRouterType } from '@nx-frontend/shared/utils';
import { goToSettings } from './settings';

export interface CreatePortalParams {
  router: NextRouterOptions;
  organization: Org;
  workspaceId: string;
  portalsUsage: PortalServicePortalsUsageDto;
  orgPortals: PortalServicePortalDto[] | null;
  isFreePlan: boolean;
  isBusinessTrialActive: boolean;
  createWorkspace?: PortalCreateWorkspaceParams;
  openModal: OpenModalFn;
  closeModal: (payload: void | ModalState) => void | ModalState;
  opCanCreatePortal: boolean;
  opCanManageBilling: boolean;
  getOpenPaywallPopup: (params: { openModal: OpenModalFn; payload: PaywallPopupPayload }) => void;
  openPortalAddonPupup: () => void;
}

export const showCreateWorkspaceError = (openModal: OpenModalFn) => {
  const createWorkspaceErrorPopupPayload: DialogPopupPayload = {
    payloadType: PopupType.DIALOG,
    text: 'Please try again in a few moments.',
    buttons: [
      {
        text: 'OK',
      },
    ],
  };

  const createWorkspaceErrorPopup = {
    title: 'Unable to Create Space',
    content: PopupType.DIALOG,
    showCloseButton: true,
    closeButtonSize: 18,
    payload: createWorkspaceErrorPopupPayload,
  };

  openModal(createWorkspaceErrorPopup);
};

/**
 * @deprecated Use `useNavigateToCreatePortal`
 */
export function createPortal(params: CreatePortalParams): boolean {
  const {
    router,
    organization,
    workspaceId,
    portalsUsage,
    orgPortals,
    isFreePlan,
    isBusinessTrialActive,
    opCanCreatePortal,
    openModal,
    closeModal,
    getOpenPaywallPopup,
    openPortalAddonPupup,
    opCanManageBilling,
  } = params;
  const wizardUrl = getPortalWizardUrl(generalEnv.appNimbusDomain || generalEnv.nimbusDomain, workspaceId);

  const noPermissionsPopupPayload: DialogPopupPayload = {
    payloadType: PopupType.DIALOG,
    text: 'Only the organization owner or managers can launch the portal setup.',
    buttons: [
      {
        text: 'OK',
      },
    ],
  };

  const noPermissionsPopup = {
    title: 'No permissions to launch the portal',
    content: PopupType.DIALOG,
    showCloseButton: true,
    closeButtonSize: 18,
    payload: noPermissionsPopupPayload,
  };

  const workspaceHasPortalPopupPayload: DialogPopupPayload = {
    payloadType: PopupType.DIALOG,
    text: 'Do you want to create a new workspace and create a portal in it?',
    buttons: [
      {
        text: 'Cancel',
      },
      {
        text: 'Create a workspace',
        main: true,
        onClick: () => {
          goToSettings(router, '/org/' + organization.globalId + '/ws/add');
          closeModal();
        },
      },
    ],
  };

  const workspaceHasPortalPopup = {
    title: 'The workspace already has a portal',
    content: PopupType.DIALOG,
    showCloseButton: true,
    closeButtonSize: 18,
    payload: workspaceHasPortalPopupPayload,
  };

  if (!opCanCreatePortal) {
    openModal(noPermissionsPopup);
    return false;
  }

  let workspacePortal = null;
  if (orgPortals && orgPortals.length) {
    workspacePortal = orgPortals.find((portal) => portal.workspaceId === workspaceId);
  }

  if (workspacePortal) {
    openModal(workspaceHasPortalPopup);
    return false;
  }

  const { active, limit } = portalsUsage;

  const needToPurchaseSubscription = () => {
    const popupPayload: PaywallPopupPayload = {
      payloadType: PopupType.PAYWALL,
      returnUrl: wizardUrl,
    };
    if (opCanManageBilling) {
      getOpenPaywallPopup({ openModal, payload: popupPayload });
    } else {
      openModal(needOwnerToPurchasePortalPopup);
    }
  };

  if (active >= limit) {
    if (isFreePlan) {
      needToPurchaseSubscription();
      return false;
    }

    if (!opCanCreatePortal) {
      openModal(noPermissionsPopup);
      return false;
    }

    if (isBusinessTrialActive) {
      needToPurchaseSubscription();
    } else {
      openPortalAddonPupup();
    }
    return false;
  }

  if (wizardUrl) {
    window.open(wizardUrl, '_self');
  }

  return true;
}

export interface NewPortalWorkspaceClickActionParams {
  workspace: Workspace | null;
  workspacePortal?: PortalServicePortalDto;
  portalsUsage: PortalServicePortalsUsageDto;
  orgUsage: {
    data: OrgServiceOrgUsageDto | null;
  };
  currentOrganization: Org;
  role: OrgMemberRoleAndPrivileges;
  routerType: NextRouterType;
  orgPortals: PortalServicePortalDto[] | null;
  isFreePlan: boolean;
  isBusinessTrialActive: boolean;
  opCanCreatePortal: boolean;
  opCanManageBilling: boolean;
  openModal: OpenModalFn;
  routerPushFn: NextRouterPushFunction;
  closeModal: (payload: void | ModalState) => void | ModalState;
  getOpenPaywallPopup: (params: { openModal: OpenModalFn; payload: PaywallPopupPayload }) => void;
  openPortalAddonPupup: () => void;
}

/**
 * @deprecated Use `useNewPortalWorkspaceClickAction`
 */
export const newPortalWorkspaceClickAction = async (params: NewPortalWorkspaceClickActionParams) => {
  let { workspace } = params;
  const {
    workspacePortal,
    portalsUsage,
    orgUsage,
    currentOrganization,
    routerType,
    orgPortals,
    isFreePlan,
    isBusinessTrialActive,
    opCanCreatePortal,
    opCanManageBilling,
    openModal,
    routerPushFn,
    closeModal,
    getOpenPaywallPopup,
    openPortalAddonPupup,
  } = params;

  if (workspacePortal) {
    console.log('Workspace already has portal');
    return;
  }

  if (!portalsUsage) {
    console.warn('Empty portalsUsage');
    return;
  }

  let currentWorkspaces = 0;
  if (orgUsage && orgUsage.data && orgUsage.data.workspaces) {
    currentWorkspaces = orgUsage.data.workspaces.current;
  }
  const createWorkspace: PortalCreateWorkspaceParams = {
    shouldByPortalAddon: false,
    orgId: currentOrganization.globalId,
    title: `${currentOrganization.title} Portal ${currentWorkspaces + 1}`,
  };
  if (!workspace) {
    if (opCanCreatePortal) {
      if (portalsUsage.active < portalsUsage.limit) {
        try {
          workspace = (
            await axiosClient.post<Workspace>(`/v2/api/workspaces/create`, {
              orgId: createWorkspace.orgId,
              title: createWorkspace.title,
              skipLimits: true,
            })
          ).data;
        } catch (err) {
          showCreateWorkspaceError(openModal);
          return;
        }
      } else {
        createWorkspace.shouldByPortalAddon = true;
      }
    }
  }

  if (!createWorkspace.shouldByPortalAddon && (!workspace || !workspace.globalId)) {
    console.warn('Empty workspaceId');
    return;
  }

  createPortal({
    router: { routerType, routerPushFn },
    organization: currentOrganization,
    workspaceId: createWorkspace.shouldByPortalAddon ? '' : workspace ? workspace.globalId : '',
    createWorkspace,
    portalsUsage,
    orgPortals,
    isFreePlan,
    isBusinessTrialActive,
    opCanCreatePortal,
    opCanManageBilling,
    openModal,
    closeModal,
    getOpenPaywallPopup,
    openPortalAddonPupup,
  });
};
